/**
 * Pointer interaction for the scene editor, kept out of React so that a drag
 * only touches the project store (coalesced into one undo step) and a small
 * transient store for guides and the marquee.
 */
import { createStore, type StoreApi } from 'zustand/vanilla';
import type { Id, ItemTransform, SceneItem, Vec2 } from '../../core/model';
import type { StudioApi } from '../../engine/api';
import type { Actions } from '../actions';
import { attempt } from '../safe';
import type { UiStore } from '../uiStore';
import { cropTransform, handlePosition, moveTransform, resizeTransform, rotateTransform, type Handle } from './dragMath';
import {
  aabbOf,
  cycleSelection,
  itemsAtPoint,
  itemsInRect,
  placeItems,
  toParentDelta,
  toParentSpace,
  unionAabb,
  type Aabb,
  type PlacedItem,
} from './sceneLayout';
import { collectTargets, snapBox, snapPoint, type Guide, type SnapTargets } from './snapping';
import { screenToCanvas, type Viewport } from './viewport';

export interface EditorTransient {
  guides: Guide[];
  marquee: Aabb | null;
  dragging: boolean;
}

export type HandleRef = { type: 'resize'; handle: Handle } | { type: 'rotate' };

export interface PointerInfo {
  /** Position inside the editor element, CSS pixels. */
  point: Vec2;
  button: number;
  alt: boolean;
  shift: boolean;
  /** Ctrl or Cmd. */
  ctrl: boolean;
}

export interface EditorDeps {
  studio: StudioApi;
  ui: UiStore;
  actions: Actions;
  getSceneId(): Id | null;
  getViewport(): Viewport;
  panTo(viewport: Viewport): void;
}

interface MovingItem {
  id: Id;
  start: ItemTransform;
  parentMatrix: PlacedItem['parentMatrix'];
}

type Drag =
  | { kind: 'pan'; startPoint: Vec2; startViewport: Viewport }
  | { kind: 'marquee'; start: Vec2; base: Id[] }
  | { kind: 'move'; start: Vec2; items: MovingItem[]; box: Aabb | null; targets: SnapTargets; moved: boolean; collapseTo: Id | null }
  | { kind: 'resize' | 'crop'; start: Vec2; target: MovingItem; size: Vec2; handle: Handle; handleStart: Vec2; targets: SnapTargets }
  | { kind: 'rotate'; start: Vec2; target: MovingItem };

/** Pointer travel (screen px) before a press on an item becomes a move. */
const DRAG_SLOP = 3;

export interface EditorController {
  readonly transient: StoreApi<EditorTransient>;
  setSpaceHeld(held: boolean): void;
  isSpaceHeld(): boolean;
  pointerDown(info: PointerInfo, handle: HandleRef | null): void;
  pointerMove(info: PointerInfo): void;
  pointerUp(): void;
  /** Abandons the drag in progress and puts the dragged items back. Returns false when nothing was being dragged. */
  cancel(): boolean;
  doubleClick(info: PointerInfo): void;
  /** Returns the item under the pointer (selecting it if needed), or null for empty canvas. */
  contextTarget(info: PointerInfo): Id | null;
  placed(): PlacedItem[];
}

export function createEditorController(deps: EditorDeps): EditorController {
  const { studio, ui, actions } = deps;
  const transient = createStore<EditorTransient>(() => ({ guides: [], marquee: null, dragging: false }));
  let drag: Drag | null = null;
  let spaceHeld = false;
  let dragSerial = 0;
  /** Scene the drag started in; the edited scene can change underneath a held pointer. */
  let dragSceneId: Id | null = null;

  const project = () => studio.project.getState().project;
  const scene = () => {
    const id = deps.getSceneId();
    return id ? (project().scenes[id] ?? null) : null;
  };
  const sizeOf = (item: SceneItem): Vec2 => actions.contentSize(item.id);
  const placed = (): PlacedItem[] => {
    const current = scene();
    return current ? placeItems(current.items, sizeOf) : [];
  };
  const canvasPoint = (info: PointerInfo): Vec2 => screenToCanvas(deps.getViewport(), info.point);
  const selectionSet = (): Set<Id> => new Set(ui.getState().selection);

  const snapTargets = (all: readonly PlacedItem[], moving: ReadonlySet<Id>): SnapTargets => {
    const prefs = ui.getState().prefs;
    const video = project().video;
    const others = all
      .filter((p) => p.depth === 0 && p.visible && !moving.has(p.item.id))
      .map((p) => aabbOf(p.corners));
    return collectTargets({ x: video.width, y: video.height }, others, {
      canvas: prefs.snapToCanvas,
      center: prefs.snapToCenter,
      items: prefs.snapToItems,
    });
  };

  const snapThreshold = (info: PointerInfo): number => {
    const prefs = ui.getState().prefs;
    // Holding Ctrl suspends snapping for fine placement.
    return prefs.snapEnabled && !info.ctrl ? prefs.snapDistance / deps.getViewport().scale : 0;
  };

  // Live edits stay out of history: the store closes a coalesced step after a short idle period, so a
  // drag with a pause in it would otherwise become several undo steps. `settle` records the single step.
  const apply = (id: Id, patch: Partial<ItemTransform>): void => {
    const sceneId = dragSceneId;
    if (!sceneId || Object.keys(patch).length === 0) return;
    attempt(studio, () => studio.commands.setItemTransform(sceneId, id, patch, { undoable: false }));
  };

  const beginDrag = (next: Drag): void => {
    drag = next;
    dragSceneId = deps.getSceneId();
    ui.getState().setGesture(next.kind !== 'pan' && next.kind !== 'marquee');
    transient.setState({ dragging: true });
  };

  const endDrag = (): void => {
    ui.getState().setGesture(false);
    transient.setState({ guides: [], marquee: null, dragging: false });
  };

  /** False once an item being dragged has been removed from under the pointer (a plugin, another view). */
  const stillThere = (current: Drag): boolean => {
    const sceneId = dragSceneId;
    if (current.kind === 'pan' || current.kind === 'marquee') return true;
    if (!sceneId) return false;
    const targets = current.kind === 'move' ? current.items : [current.target];
    return targets.every((entry) => studio.queries.findItem(project(), sceneId, entry.id) !== null);
  };

  /** Ends a transform drag: rewinds the live edits, then (unless cancelled) re-applies the result as one undo step. */
  const settle = (finished: Drag, keep: boolean): void => {
    const sceneId = dragSceneId;
    if (!sceneId || finished.kind === 'pan' || finished.kind === 'marquee') return;
    const edited = (finished.kind === 'move' ? finished.items : [finished.target]).flatMap((entry) => {
      const end = studio.queries.findItem(project(), sceneId, entry.id)?.item.transform;
      // Compared by value: a drag that ends where it began must not leave an empty undo step.
      return end && JSON.stringify(end) !== JSON.stringify(entry.start) ? [{ id: entry.id, start: entry.start, end }] : [];
    });
    if (edited.length === 0) return;
    attempt(studio, () => {
      for (const entry of edited) studio.commands.setItemTransform(sceneId, entry.id, entry.start, { undoable: false });
      if (!keep) return;
      const coalesceKey = `editor-drag:${++dragSerial}`;
      for (const entry of edited) studio.commands.setItemTransform(sceneId, entry.id, entry.end, { coalesceKey });
    });
    studio.project.sealHistory();
  };

  const moving = (entry: PlacedItem): MovingItem => ({
    id: entry.item.id,
    start: entry.item.transform,
    parentMatrix: entry.parentMatrix,
  });

  return {
    transient,
    placed,
    setSpaceHeld(held) {
      spaceHeld = held;
    },
    isSpaceHeld: () => spaceHeld,

    pointerDown(info, handle) {
      if (drag) return;
      if (info.button === 1 || (info.button === 0 && spaceHeld)) {
        beginDrag({ kind: 'pan', startPoint: info.point, startViewport: deps.getViewport() });
        return;
      }
      if (info.button !== 0) return;
      const all = placed();
      const selection = selectionSet();
      const point = canvasPoint(info);

      if (handle) {
        const entry = all.find((p) => selection.has(p.item.id));
        if (!entry || entry.locked) return;
        const target = moving(entry);
        if (handle.type === 'rotate') {
          beginDrag({ kind: 'rotate', start: point, target });
          return;
        }
        const handleStart = handlePosition(entry.item.transform, entry.size, handle.handle);
        beginDrag({
          kind: info.alt ? 'crop' : 'resize',
          start: point,
          target,
          size: entry.size,
          handle: handle.handle,
          handleStart,
          targets: snapTargets(all, new Set([entry.item.id])),
        });
        return;
      }

      const hits = itemsAtPoint(all, point, selection);
      if (info.alt) {
        const next = cycleSelection(hits, selection);
        ui.getState().select(next ? [next] : []);
        return;
      }
      if (hits.length === 0) {
        const base = info.shift || info.ctrl ? [...selection] : [];
        if (base.length === 0) ui.getState().select([]);
        beginDrag({ kind: 'marquee', start: point, base });
        return;
      }
      const top = hits.find((h) => selection.has(h.item.id)) ?? hits[0]!;
      const id = top.item.id;
      if (info.shift || info.ctrl) {
        ui.getState().select(selection.has(id) ? [...selection].filter((s) => s !== id) : [...selection, id]);
        return;
      }
      let collapseTo: Id | null = null;
      if (!selection.has(id)) ui.getState().select([id]);
      else if (selection.size > 1) collapseTo = id;

      const active = selectionSet();
      const entries = all.filter((p) => active.has(p.item.id) && !p.locked);
      beginDrag({
        kind: 'move',
        start: point,
        items: entries.map(moving),
        box: unionAabb(entries.map((e) => aabbOf(e.corners))),
        targets: snapTargets(all, active),
        moved: false,
        collapseTo,
      });
    },

    pointerMove(info) {
      if (!drag) return;
      if (!stillThere(drag)) {
        this.cancel();
        return;
      }
      if (drag.kind === 'pan') {
        deps.panTo({
          scale: drag.startViewport.scale,
          offsetX: drag.startViewport.offsetX + info.point.x - drag.startPoint.x,
          offsetY: drag.startViewport.offsetY + info.point.y - drag.startPoint.y,
        });
        return;
      }
      const point = canvasPoint(info);
      const threshold = snapThreshold(info);

      if (drag.kind === 'marquee') {
        const rect = aabbOf([drag.start, point]);
        transient.setState({ marquee: rect });
        ui.getState().select([...new Set([...drag.base, ...itemsInRect(placed(), rect)])]);
        return;
      }

      if (drag.kind === 'move') {
        let delta = { x: point.x - drag.start.x, y: point.y - drag.start.y };
        if (!drag.moved) {
          const scale = deps.getViewport().scale;
          if (Math.hypot(delta.x * scale, delta.y * scale) < DRAG_SLOP) return;
          drag.moved = true;
        }
        let guides: Guide[] = [];
        if (threshold > 0 && drag.box) {
          const moved = {
            minX: drag.box.minX + delta.x,
            maxX: drag.box.maxX + delta.x,
            minY: drag.box.minY + delta.y,
            maxY: drag.box.maxY + delta.y,
          };
          const snap = snapBox(moved, drag.targets, threshold);
          delta = { x: delta.x + snap.dx, y: delta.y + snap.dy };
          guides = snap.guides;
        }
        for (const item of drag.items) {
          const local = toParentDelta(item.parentMatrix, delta);
          if (local) apply(item.id, moveTransform(item.start, local));
        }
        transient.setState({ guides });
        return;
      }

      if (drag.kind === 'rotate') {
        const from = toParentSpace(drag.target.parentMatrix, drag.start);
        const to = toParentSpace(drag.target.parentMatrix, point);
        if (from && to) apply(drag.target.id, rotateTransform(drag.target.start, from, to, info.shift));
        return;
      }

      // Resize and crop follow the handle, not the raw pointer, so the grab offset is preserved.
      const startParent = toParentSpace(drag.target.parentMatrix, drag.start);
      const nowParent = toParentSpace(drag.target.parentMatrix, point);
      if (!startParent || !nowParent) return;
      if (drag.kind === 'crop') {
        apply(drag.target.id, cropTransform(drag.target.start, drag.size, drag.handle, startParent, nowParent));
        return;
      }
      let handleParent = {
        x: drag.handleStart.x + nowParent.x - startParent.x,
        y: drag.handleStart.y + nowParent.y - startParent.y,
      };
      let guides: Guide[] = [];
      // Snapping is defined in canvas space; it is only meaningful for unrotated top-level items.
      const axisAligned = drag.target.start.rotation % 90 === 0 && drag.target.parentMatrix.b === 0 && drag.target.parentMatrix.c === 0;
      if (threshold > 0 && axisAligned) {
        const m = drag.target.parentMatrix;
        const world = { x: m.a * handleParent.x + m.tx, y: m.d * handleParent.y + m.ty };
        const sideways = Math.abs(drag.target.start.rotation % 180) === 90;
        const axes = sideways
          ? { x: drag.handle.y !== 0.5, y: drag.handle.x !== 0.5 }
          : { x: drag.handle.x !== 0.5, y: drag.handle.y !== 0.5 };
        const snap = snapPoint(world, drag.targets, threshold, axes);
        handleParent = { x: handleParent.x + snap.dx / (m.a || 1), y: handleParent.y + snap.dy / (m.d || 1) };
        guides = snap.guides;
      }
      apply(
        drag.target.id,
        resizeTransform(drag.target.start, drag.size, drag.handle, handleParent, { keepAspect: !info.shift }),
      );
      transient.setState({ guides });
    },

    pointerUp() {
      if (!drag) return;
      const finished = drag;
      drag = null;
      if (finished.kind === 'move' && !finished.moved && finished.collapseTo) ui.getState().select([finished.collapseTo]);
      settle(finished, true);
      endDrag();
    },

    cancel() {
      if (!drag) return false;
      const abandoned = drag;
      drag = null;
      settle(abandoned, false);
      if (abandoned.kind === 'marquee') ui.getState().select(abandoned.base);
      endDrag();
      return true;
    },

    doubleClick(info) {
      const hits = itemsAtPoint(placed(), canvasPoint(info), selectionSet());
      const top = hits[0];
      if (!top) return;
      ui.getState().select([top.item.id]);
      if (top.item.type === 'source') actions.openProperties(top.item.id);
      else if (top.item.type === 'scene') actions.openFilters(top.item.id);
    },

    contextTarget(info) {
      const selection = selectionSet();
      const hits = itemsAtPoint(placed(), canvasPoint(info), selection);
      const top = hits.find((h) => selection.has(h.item.id)) ?? hits[0];
      if (!top) return null;
      if (!selection.has(top.item.id)) ui.getState().select([top.item.id]);
      return top.item.id;
    },
  };
}
